import React, { useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import Quote from '@editorjs/quote';

// Utility: safely escape HTML for text nodes
function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const renderEditorDataToHTML = function(input) {
    if (!input) return '';

    let data = input;
    if (typeof input === 'string') {
        try { data = JSON.parse(input); } catch (_) { return ''; }
    }

    const blocks = Array.isArray(data?.blocks) ? data.blocks : [];
    if (blocks.length === 0) return '';

    const html = blocks.map((block) => {
        if (!block || typeof block !== 'object') return '';
        const { type, data } = block;
        switch (type) {
            case 'header': {
                const level = Math.min(Math.max(parseInt(data?.level, 10) || 2, 1), 6);
                const text = data?.text || '';
                // EditorJS header text may contain inline formatting HTML; keep as-is
                return `<h${level}>${text}</h${level}>`;
            }
            case 'paragraph': {
                const text = data?.text || '';
                return `<p>${text}</p>`;
            }
            case 'list': {
                const style = (data?.style === 'ordered') ? 'ol' : 'ul';
                const items = Array.isArray(data?.items) ? data.items : [];
                const itemsHtml = items.map(it => {
                    const text = typeof it === 'string' ? it : (it?.content || '');
                    return `<li>${text}</li>`;
                }).join('');
                return `<${style}>${itemsHtml}</${style}>`;
            }
            case 'quote': {
                const text = data?.text || '';
                const caption = data?.caption ? `<cite>${data.caption}</cite>` : '';
                return `<blockquote><p>${text}</p>${caption}</blockquote>`;
            }
            case 'image': {
                let url = data?.file?.url || data?.url || '';
                if (!url) return '';
                // If URL is relative, prefix with configured API URL
                if (!/^https?:\/\//i.test(url)) {
                    const base = process.env.REACT_APP_API_URL || '';
                    if (base) {
                        if (base.endsWith('/') && url.startsWith('/')) {
                            url = base + url.substring(1);
                        } else if (!base.endsWith('/') && !url.startsWith('/')) {
                            url = base + '/' + url;
                        } else {
                            url = base + url;
                        }
                    }
                }
                const caption = data?.caption || '';
                const alt = escapeHtml(caption || '');
                const figure = `<img src="${escapeHtml(url)}" alt="${alt}"/>` + (caption ? `<figcaption>${caption}</figcaption>` : '');
                return `<figure>${figure}</figure>`;
            }
            default:
                return '';
        }
    }).join('');

    return html;
}

const Editor = ({ data, onChange, websiteId }) => {
    const editorInstance = useRef(null); // To store the Editor.js instance
    const holderId = useRef(`editor-js-holder-${Math.random().toString(36).substring(7)}`); // Unique ID for editor container
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!editorInstance.current) {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const token = localStorage.getItem('token');

            editorInstance.current = new EditorJS({
                holder: holderId.current,
                data: (data && Array.isArray(data.blocks)) ? data : { blocks: [] }, // Initial data for the editor
                tools: {
                    header: Header,
                    list: List,
                    quote: Quote,
                    image: {
                        class: ImageTool,
                        config: {
                            endpoints: {
                                byFile: `${apiUrl}public/images/${websiteId}/upload`,
                            },
                            additionalRequestHeaders: token ? { 'Authorization': `Bearer ${token}` } : {}
                        }
                    }
                },
                onChange: async () => {
                    try {
                        const savedData = await editorInstance.current.save();
                        if (onChangeRef.current) {
                            onChangeRef.current(savedData);
                        }
                    } catch (_) {
                    }
                },
            });
        }

        return () => {
            if (editorInstance.current && editorInstance.current.destroy) {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
        };
    }, [data]);

    return (
        <div id={holderId.current} className='editor-js form-control' />
    );
};

export {Editor, renderEditorDataToHTML};